import React, { useEffect, useMemo, useState, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "./../../assets/css/detail-annonces/DetailAnnonce.css";
import Breadcrumb from "../../components/breadcrumb/Breadcrumb";
import useArticles from "../../hooks/useArticle";
import useArticleEvent from "../../hooks/useArticleEvent.js";
import { truncateText } from "../../utils/formatTexte";
import { formatPrice } from "../../utils/formatPrice";
import { imageArticleUrl } from "../../utils/modules";
import whatsappLogo from "./../../assets/images/logo/whatshapp.png";
import defaultAnnonce from "../../assets/images/articles/no-photo.png";
import { AuthContext } from "../../contexts/AuthContext.js";
import LoaderDetailAnnonce from "../../components/loader/article/LoaderDetailAnnonce.jsx";
import { Helmet } from "react-helmet-async";

export default function ArticlesDetail({ setAlert }) {
  const { user } = useContext(AuthContext);
  const currentUser =
    JSON.parse(localStorage.getItem("user")) ||
    JSON.parse(sessionStorage.getItem("user"));
  const { id } = useParams();
  const navigate = useNavigate();

  const [article, setArticle] = useState(null);
  const [loadingOne, setLoadingOne] = useState(true);
  const [active, setActive] = useState(0);
  const [imgVersion, setImgVersion] = useState(Date.now());
  const [showNum, setShowNum] = useState(false);
  const { findOneArticle, updateArticleCounter } = useArticles();
  const [isOwner, setIsOwner] = useState(false);
  const { trackArticleEvent } = useArticleEvent();

  // const isOwner =
  //   !!user?._id && article?.idUtilisateur?._id === user?._id;
  useEffect(() => {
    let mounted = true;
    if (!id) return;

    setLoadingOne(true);
    setArticle(null);

    findOneArticle(id)
      .then((res) => {
        if (!mounted) return;

        const data = res?.data?.data || null;
        setArticle(data);
        // console.log(user?._id, data?.idUtilisateur, currentUser?._id);

        if (
          data?.idUtilisateur == user?._id ||
          data?.idUtilisateur == currentUser?._id
        ) {
          setIsOwner(true);
        }
        setImgVersion(Date.now());
        setLoadingOne(false);
      })
      .catch((err) => {
        if (!mounted) return;

        setLoadingOne(false);
        setArticle(null);

        setAlert?.({
          message:
            "Une erreur s'est produite lors de la récupération des données de l'annonce.",
          type: "danger",
        });

        console.error("Erreur lors de la récupération de l'article :", err);
      });
    updateArticleCounter?.(id, "nbVue").catch((err) => console.log(err));
    trackArticleEvent(id, 1).catch((err) => console.log(err)); // ➕ 1 = VUE

    return () => {
      mounted = false;
    };
  }, [id]);

  const formatPhotoPath = (p) => (p || "").replace(/\\/g, "/");

  const buildPhotoUrl = (storedPath) => {
    const p = formatPhotoPath(storedPath);
    if (!p) return "";
    if (p.startsWith("http://") || p.startsWith("https://")) return p;

    const idx = p.indexOf("files/");
    const relative = idx !== -1 ? p.slice(idx) : p;
    return `${imageArticleUrl}/${relative}?v=${imgVersion}`;
  };

  const images = useMemo(() => {
    const pics = article?.photos || [];
    return pics.map(buildPhotoUrl).filter(Boolean);
  }, [article, imgVersion]);

  useEffect(() => {
    setActive(0);
  }, [article?._id]);

  const thumbs = useMemo(() => {
    if (!images?.length) return [];
    const next1 = (active + 1) % images.length;
    const next2 = (active + 2) % images.length;
    return [next1, next2];
  }, [active, images]);

  const useCarousel = (images?.length || 0) > 1;

  const goPrev = () => {
    if (!images?.length) return;
    setActive((p) => (p - 1 + images.length) % images.length);
  };

  const goNext = () => {
    if (!images?.length) return;
    setActive((p) => (p + 1) % images.length);
  };

  const openImagesViewer = () => {
    if (!images?.length) return;

    navigate(`/article/${id}/photos`, {
      state: {
        images,
        startIndex: active,
        from: `/article/detail/${id}`,
      },
    });
  };

  const breadcrumbItems = useMemo(() => {
    const title = article?.titre
      ? `${truncateText(article.titre, 18)}…`
      : "Détails";

    return [
      { label: "Accueil", link: "/" },
      { label: article?.groupeCategorie?.nom },
      {
        label: article?.categorie?.nom,
        link: `/article/resultat?idCategorie=${article?.categorie?._id}`,
      },
      { label: title },
    ];
  }, [article?.titre]);

  const normalizePhoneForWhatsApp = (rawPhone) => {
    if (!rawPhone) return "";
    let digits = String(rawPhone).replace(/\D/g, "");

    if (digits.startsWith("0")) digits = "261" + digits.slice(1);

    return digits;
  };

  const buildWhatsAppUrl = (phone, message = "") => {
    const normalized = normalizePhoneForWhatsApp(phone);
    if (!normalized) return "";
    const base = `https://wa.me/${normalized}`;
    return message ? `${base}?text=${encodeURIComponent(message)}` : base;
  };

  const openWhatsApp = () => {
    updateArticleCounter(article._id, "nbWhatsapp")
      .then((res) => {})
      .catch((err) => {
        console.log(err);
        trackArticleEvent(article._id, 6).catch((err) => console.log(err));
      });

    const url = buildWhatsAppUrl(
      article?.phone,
      `Bonjour, je suis intéressé(e) par votre annonce : "${article?.titre}".`,
    );

    if (!url) {
      setAlert?.({
        type: "warning",
        message: "Numéro WhatsApp introuvable ou invalide.",
      });
      return;
    }

    window.open(url, "_blank", "noopener,noreferrer");
  };

  const showPhone = () => {
    setShowNum(true);
    updateArticleCounter(article._id, "nbAppel")
      .then((res) => {})
      .catch((err) => console.log(err));
    trackArticleEvent(article._id, 5).catch((err) => console.log(err)); // ➕ 5 = APPEL (révélation numéro)
  };

  const goToMessagePage = (id) => {
    updateArticleCounter(article._id, "nbMessage")
      .then((res) => {})
      .catch((err) => {
        console.log(err);
        trackArticleEvent(article._id, 8).catch((err) => console.log(err));
      });
    navigate(`/message/${id}`);
  };

  const imageBaseUrl = process.env.REACT_APP_ARTICLE_IMAGE;

  const buildSellerAvatar = () => {
    const p = String(article?.utilisateur?.photo || "").trim();
    if (!p || p === "/default.png")
      return `${imageBaseUrl}/files/profile/default.png`;
    if (/^https?:\/\//i.test(p)) return p;
    if (p.startsWith("/")) return `${imageBaseUrl}${p}`;
    return `${imageBaseUrl}/${p}`;
  };

  const sellerAvatar = buildSellerAvatar();

  const shareArticle = () => {
    updateArticleCounter(article._id, "nbPartageFacebook")
      .then((res) => {})
      .catch((err) => {
        console.log(err);
        trackArticleEvent(article._id, 7).catch((err) => console.log(err));
      });

    const url = `https://web.facebook.com/sharer.php?u=https://api.deal.mg/api/v1/article/share/socialMedia/${article._id}&_rdc=1&_rdr#`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const hasPrice = Number(article?.prix) > 0;

  const etatArticleLabel =
    hasPrice && Number(article?.etat) === 1
      ? "Neuf"
      : hasPrice && Number(article?.etat) === 2
        ? "Occasion"
        : "";

  if (loadingOne) {
    return (
      <div className="daPage">
        <div className="container">
          <div className="daBreadcrumbWrap">
            <Breadcrumb
              items={[{ label: "Accueil", link: "/" }, { label: "Détails" }]}
            />
          </div>

          <LoaderDetailAnnonce />
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="daPage">
        <div className="container">
          <div className="daBreadcrumbWrap">
            <Breadcrumb
              items={[{ label: "Accueil", link: "/" }, { label: "Détails" }]}
            />
          </div>

          <div style={{ padding: 18, color: "var(--gris-terciaire)" }}></div>
        </div>
      </div>
    );
  }
 document.title = `${article?.titre ? article.titre+" | Deal.mg": "Détails de l'annonce"}`;
  return (
    <>
      <Helmet>    

        <meta
          name="description"
          content={(article.description || "").slice(0, 160)}
        />

        <link
          rel="canonical"
          href={`https://deal.mg/article/details/${article._id}`}
        />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org/",
            "@type": "Product",
            name: article.titre,
            image: [
              article.photos?.[0]
                ? article.photos[0]
                : "https://deal.mg/logo.png",
            ],
            description: article.description,
            sku: article._id,
            offers: {
              "@type": "Offer",
              url: `https://deal.mg/article/details/${article._id}`,
              priceCurrency: "MGA",
              price: article.prix ? article.prix.toString() : "0",
              availability: "https://schema.org/InStock",
            },
          })}
        </script>
      </Helmet>
      <div className="daPage">
        <div className="container">
          <div className="daBreadcrumbWrap">
            <Breadcrumb items={breadcrumbItems} />
          </div>

          <div className="daGrid">
            <div className="daLeft">
              <div className="daMedia">
                <div className="daMediaStage">
                  {useCarousel && (
                    <>
                      <button
                        type="button"
                        className="daCarBtn daCarBtnLeft"
                        onClick={goPrev}
                        aria-label="Image précédente"
                      >
                        ‹
                      </button>
                      <button
                        type="button"
                        className="daCarBtn daCarBtnRight"
                        onClick={goNext}
                        aria-label="Image suivante"
                      >
                        ›
                      </button>
                    </>
                  )}

                  {images.length > 0 ? (
                    <div className="daMediaGrid">
                      <div className="daMain">
                        <img
                          className="daMainImg"
                          src={images?.[active] || defaultAnnonce}
                          alt={`Annonce ${active + 1}`}
                          onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = defaultAnnonce;
                          }}
                        />
                      </div>

                      <div className="daThumbs">
                        {thumbs.map((idx) => (
                          <div
                            key={idx}
                            className={`daThumb ${idx === active ? "active" : ""}`}
                            onClick={() => setActive(idx)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ")
                                setActive(idx);
                            }}
                            aria-label={`Voir l'image ${idx + 1}`}
                          >
                            <img
                              className="daThumbImg"
                              src={images[idx]}
                              alt={`Miniature ${idx + 1}`}
                              onError={(e) => {
                                e.currentTarget.src = defaultAnnonce;
                              }}
                            />
                          </div>
                        ))}

                        {/* ✅ Bouton Leboncoin */}
                        {/* ✅ Bouton overlay (desktop + mobile) */}
                        <button
                          type="button"
                          className="daViewImagesBtn daViewImagesBtnFloating"
                          onClick={openImagesViewer}
                          disabled={!images?.length}
                        >
                          <i className="bi bi-images" />
                          <span>Voir les images</span>
                          <em>{images?.length || 0}</em>
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div style={{ padding: 14 }}>Aucune image.</div>
                  )}
                </div>

                {/* Détails texte sous images */}
                <div className="daDetails">
                  <div className="daTitle">{article.titre}</div>
                  {article?.prix && article.prix > 0 ? (
                    <div className="daPrice">
                      {formatPrice(article.prix)} MGA
                    </div>
                  ) : null}
                  <div className="daCity">
                    {article.ville}{" "}
                    {article.quartier ? `• ${article.quartier}` : ""}
                  </div>
                  <div className="daDate">
                    {article.createdAt
                      ? new Date(article.createdAt).toLocaleString("fr-FR")
                      : ""}
                  </div>
                </div>
                {/* description */}
                <div className="daInfoBlock">
                  <div className="daInfoTitle">Description</div>

                  <div>
                    {article.description
                      ? article.description
                          .split(/\r?\n/)
                          .map((line, index) => (
                            <div
                              className="texte_description"
                              key={index}
                              style={{ marginLeft: "0 !important" }}
                            >
                              {line}
                              <br />
                            </div>
                          ))
                      : "Aucune description disponible"}
                  </div>
                </div>
                {/* Infos clés */}
                <div className="daInfoBlock d-none">
                  <div className="daInfoTitle">Les informations clés</div>

                  <div className="daInfoRows">
                    {(article.infoCle || []).length === 0 ? (
                      <div
                        style={{ fontSize: 12, color: "var(--gris-terciaire)" }}
                      >
                        Aucune information clé.
                      </div>
                    ) : (
                      (article.infoCle || []).map((it, idx) => (
                        <div className="daInfoRow" key={`${it.name}-${idx}`}>
                          <i className={`bi bi-${it.icon}`}></i>
                          <div className="daInfoKey">{it.name}</div>
                          <div className="daInfoVal">{it.value || "-"}</div>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Localisation + Signalement */}
                {article.ville && (
                  <div style={{ marginTop: 14 }}>
                    <div className="daInfoTitle">Localisation</div>

                    <div
                      style={{
                        fontSize: 14,
                        color: "var(--couleur-secondaire)",
                        lineHeight: 1.5,
                        textTransform: "uppercase",
                      }}
                    >
                      {article.ville}
                    </div>

                    <div className="article-detail-etat-partagefacebook">
                      {etatArticleLabel && (
                        <div style={{ marginTop: 14 }}>
                          <div className="daInfoTitle">État</div>

                          <div
                            style={{
                              fontSize: 14,
                              color: "var(--couleur-secondaire)",
                              lineHeight: 1.5,
                            }}
                          >
                            {etatArticleLabel}
                          </div>
                        </div>
                      )}
                      <button
                        className="daBtn btnIconFacebook"
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          shareArticle();
                        }}
                        disabled={!article?.phone}
                        title="Partager sur Facebook"
                      >
                        <i
                          class="bi bi-facebook color-icon-facebook"
                          title="Partager sur Facebook"
                        ></i>
                        <span>Partager</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* RIGHT */}
            <aside className="daRight">
              <div className="daCard">
                <div className="daSellerTop">
                  <div className="daAvatar">
                    <img
                      src={sellerAvatar}
                      alt="Seller"
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = `${process.env.REACT_APP_ARTICLE_IMAGE}/files/profile/default.png`;
                      }}
                    />
                  </div>

                  <div>
                    <div className="daSellerName">
                      {article.pseudo || "Vendeur"}
                    </div>
                  </div>
                </div>

                <button
                  className="daBtn"
                  type="button"
                  onClick={() => {
                    goToMessagePage(id);
                  }}
                >
                  Envoyer un message
                </button>

                <div style={{ height: 10 }} />

                {article.phone && (
                  <button
                    className="daBtn daBtnWhatsapp"
                    type="button"
                    onClick={openWhatsApp}
                    disabled={!article?.phone}
                    title={
                      !article?.phone
                        ? "Aucun numéro disponible"
                        : "Contacter sur WhatsApp"
                    }
                  >
                    <img
                      src={whatsappLogo}
                      alt="WhatsApp"
                      className="daWhatsappIcon"
                    />
                    <span>WhatsApp</span>
                  </button>
                )}

                {article.phone && (
                  <>
                    {showNum ? (
                      <button
                        className="daBtn daBtnWhatsapp mt-2"
                        type="button"
                        disabled={!article?.phone}
                        title="Numéro de téléphone"
                      >
                        {article.phone}
                      </button>
                    ) : (
                      <button
                        className="daBtn daBtnWhatsapp mt-2"
                        type="button"
                        onClick={showPhone}
                        disabled={!article?.phone}
                        title="Voir le numéro"
                      >
                        Voir le numéro
                      </button>
                    )}
                  </>
                )}
                <button
                  type="button"
                  className="daReportLink"
                  onClick={() => {
                    if (user) navigate(`/signalement/creation/${article?._id}`);
                    else navigate(`/connexion`);
                  }}
                >
                  Signaler l’annonce
                </button>
              </div>
              {isOwner && (
                <button
                  className="vaBtn"
                  type="button"
                  onClick={() =>
                    navigate(`/article/visualisation/${article?._id}?step=2`)
                  }
                >
                  DASHBOARD
                </button>
              )}
            </aside>
          </div>
        </div>
      </div>
    </>
  );
}
